<template>
  <div ref="dropdown" class="group relative">
    <div
      class="focus:text-gray-1 box-border flex w-full items-center justify-between overflow-hidden rounded-md border border-solid font-inter text-xs font-medium leading-[30px] outline-none transition duration-300"
      :class="{
        'border-[color-mix(in_srgb,var(--c-g100)_80%,var(--c-g10))] bg-[color-mix(in_srgb,var(--c-g100)_80%,var(--c-g10))] text-gray-400':
          inverse,
        'bg-gray-600 border-gray-600 text-gray-50': !inverse,
        '!bg-gray-100 !text-gray-400': isActive && inverse,
        '!bg-gray-800 !text-gray-10': isActive && !inverse,
        'outline outline-2 outline-accent-1 outline-offset-[-1px]':
          isActive || isOpen,
      }"
    >
      <div
        class="grid items-center h-[30px] w-[30px] justify-center transition duration-300 cursor-pointer"
        :class="{
          'bg-[color-mix(in_srgb,var(--c-g100)_80%,var(--c-g10))] hover:bg-[color-mix(in_srgb,var(--c-g200)_40%,var(--c-g10))] text-gray-400':
            inverse,
          'bg-gray-500 hover:bg-gray-400 text-gray-50': !inverse,
          '!bg-gray-800': isActive && !inverse,
          '!bg-[color-mix(in_srgb,var(--c-g100)_15%,var(--c-g10))] !text-gray-500':
            isActive && inverse,
        }"
        v-tooltip="{ content: tooltip }"
        @click="clickToSet"
      >
        <slot />
      </div>
      <div
        class="grid cursor-pointer items-center justify-center px-1 h-[30px] transition duration-300"
        :class="{
          'bg-[color-mix(in_srgb,var(--c-g100)_80%,var(--c-g10))] hover:bg-[color-mix(in_srgb,var(--c-g200)_40%,var(--c-g10))] focus:bg-[color-mix(in_srgb,var(--c-g100)_15%,var(--c-g10))] focus:text-gray-800 disabled:!brigh text-gray-400':
            inverse,
          'bg-gray-500 hover:bg-gray-400 text-gray-50': !inverse,
        }"
        @click="isOpen = !isOpen"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 320 512"
          width="7.5"
          height="12"
        >
          <path
            d="M137.4 374.6c12.5 12.5 32.8 12.5 45.3 0l128-128c9.2-9.2 11.9-22.9 6.9-34.9s-16.6-19.8-29.6-19.8L32 192c-12.9 0-24.6 7.8-29.6 19.8s-2.2 25.7 6.9 34.9l128 128z"
            fill="currentColor"
          />
        </svg>
      </div>
    </div>
    <div
      v-if="isOpen"
      class="absolute w-max rounded-md border border-solid p-1 shadow-md z-[100] top-[calc(100%+4px)]"
      :class="{
        'bg-gray-10 border-[color-mix(in_srgb,var(--c-g100)_60%,var(--c-g10))]':
          inverse,
        'bg-gray-900 border-gray-600': !inverse,
      }"
    >
      <div v-for="(items, index) in splitOptions" :key="index" class="flex">
        <div
          v-for="item in items"
          :key="item.icon"
          class="p-1 cursor-pointer leading-[0] rounded-md transition duration-300"
          :class="{
            'bg-gray-10 hover:bg-[color-mix(in_srgb,var(--c-g100)_40%,var(--c-g10))] text-gray-300 hover:text-gray-900':
              inverse,
            'bg-gray-900 hover:bg-gray-400 text-gray-100 hover:text-gray-50':
              !inverse,
            '!bg-gray-600 !text-gray-10': item.isActive && !inverse,
            '!bg-[color-mix(in_srgb,var(--c-g100)_60%,var(--c-g10))] text-gray-500':
              item.isActive && inverse,
          }"
          @click="item.set"
        >
          <component :is="components[item.icon]" class="w-8 h-8" />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onClickOutside } from "@vueuse/core";
import { ref, computed } from "vue";

export type DropOption = {
  icon: string;
  set: () => void;
  isActive: boolean;
};

const props = defineProps<{
  options: DropOption[];
  isActive: boolean;
  components: Record<string, any>;
  inverse?: boolean;
  tooltip?: string;
  set: () => void;
}>();

const isOpen = ref(false);
const dropdown = ref<HTMLElement>();

const splitOptions = computed(() => {
  const splitArray: DropOption[][] = [];
  const chunkSize = 3; // Adjust the chunk size as needed
  for (let i = 0; i < props.options.length; i++) {
    const arrayIndex = Math.floor(i / chunkSize);
    if (!splitArray[arrayIndex]) {
      splitArray[arrayIndex] = [];
    }
    splitArray[arrayIndex].push(props.options[i]);
  }
  return splitArray;
});

function clickToSet() {
  props.set();
  isOpen.value = false;
}

onClickOutside(dropdown, () => {
  isOpen.value = false;
});

function clickToOption(option: DropOption) {
  option.set();
  isOpen.value = false;
}
</script>
